import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, IconButton, Tooltip, Alert, CircularProgress
} from '@mui/material';
import {
  AccountBalance, TrendingUp, Warning, CheckCircle,
  Visibility, Payment, Receipt, RefreshOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function CuentasPorCobrar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resumenCartera, setResumenCartera] = useState(null);
  const [clientesConSaldo, setClientesConSaldo] = useState([]);
  const [facturasPendientes, setFacturasPendientes] = useState([]);

  const getConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setLoading(true);
    setError(null);
    const config = getConfig();
    try {
      const [resumenRes, clientesRes, cobrosRes, facturasRes, recibosRes] = await Promise.all([
        axios.get(`${API_URL}/api/cuentas-cobrar/resumen-cartera`, config).catch(() => ({ data: { data: null } })),
        axios.get(`${API_URL}/api/cuentas-cobrar/clientes-con-saldo`, config).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/api/cuentas-cobrar/cobros`, config).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/api/facturas`, config).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/recibos`, config).catch(() => ({ data: { data: [] } })),
      ]);

      setResumenCartera(resumenRes.data?.data || resumenRes.data);
      setClientesConSaldo(clientesRes.data?.data || []);

      // Construir lista de facturas pendientes con saldo calculado
      const todas = facturasRes.data || [];
      const recibos = recibosRes.data?.data || [];
      const cobros = cobrosRes.data?.data || cobrosRes.data || [];
      const hoy = new Date();

      // Parsear fecha en formato dd/mm/yyyy o ISO (yyyy-mm-dd / ISO string)
      const parseFecha = (str) => {
        if (!str) return null;
        // dd/mm/yyyy
        const ddmmyyyy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (ddmmyyyy) return new Date(Number(ddmmyyyy[3]), Number(ddmmyyyy[2]) - 1, Number(ddmmyyyy[1]));
        // ISO o yyyy-mm-dd
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : d;
      };

      const pendientes = todas
        .filter(f => f.estadoSRI === 'AUTORIZADO')
        .map(f => {
          const total = parseFloat(f.totales?.valorPagar || f.totales?.importeTotal || 0);
          const cobrosF = cobros.filter(c => c.factura_id === (f.id || f.claveAcceso) || c.factura_clave === f.claveAcceso);
          const cobrado = cobrosF.reduce((s, c) =>
            s + parseFloat(c.monto || 0) + parseFloat(c.ret_fuente_monto || 0) + parseFloat(c.ret_iva_monto || 0), 0);
          const saldo = Math.max(0, total - cobrado);
          const fechaObj = parseFecha(f.fechaEmision) || parseFecha(f.created_at);
          const diasTranscurridos = fechaObj
            ? Math.floor((hoy - fechaObj) / (1000 * 60 * 60 * 24))
            : 0;
          return { ...f, total, cobrado, saldo, diasTranscurridos };
        })
        .filter(f => f.saldo > 0.01)
        .sort((a, b) => b.diasTranscurridos - a.diasTranscurridos);

      // Incluir recibos a crédito como documentos pendientes de cobro
      const FORMAS_CREDITO_RECIBO = ['credito_plazo', 'credito', 'Crédito', 'CREDITO'];
      const recibosPendientes = recibos
        .filter(r => r.estado !== 'ANULADO' && FORMAS_CREDITO_RECIBO.includes(r.forma_pago))
        .map(r => {
          const total = parseFloat(r.totales?.total || 0);
          const cobrosR = cobros.filter(c => c.factura_id === r.id);
          const cobrado = cobrosR.reduce((s, c) =>
            s + parseFloat(c.monto || 0) + parseFloat(c.ret_fuente_monto || 0) + parseFloat(c.ret_iva_monto || 0), 0);
          const saldo = Math.max(0, total - cobrado);
          const fechaObj = parseFecha(r.fecha);
          const diasTranscurridos = fechaObj
            ? Math.floor((hoy - fechaObj) / (1000 * 60 * 60 * 24))
            : 0;
          return {
            id: r.id,
            tipo_documento: 'RECIBO',
            numero_recibo: r.numero,
            cliente: r.cliente,
            fechaEmision: r.fecha ? new Date(r.fecha).toLocaleDateString('es-EC') : '—',
            created_at: r.fecha,
            totales: r.totales,
            total, cobrado, saldo, diasTranscurridos,
          };
        })
        .filter(r => r.saldo > 0.01);

      setFacturasPendientes([...pendientes, ...recibosPendientes].sort((a, b) => b.diasTranscurridos - a.diasTranscurridos));
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  const getChipEdad = (dias) => {
    const d = Math.max(0, dias);
    if (d <= 30) return { label: `${d}d`, color: 'success' };
    if (d <= 60) return { label: `${d}d`, color: 'warning' };
    if (d <= 90) return { label: `${d}d`, color: 'error' };
    return { label: `${d}d`, color: 'error' };
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
      <CircularProgress />
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* Título */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Cuentas por Cobrar</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={cargarDatos}>Actualizar</Button>
          <Button variant="contained" startIcon={<Payment />}
            onClick={() => navigate('/cuentas-cobrar/registrar-cobro')}>
            Registrar Cobro
          </Button>
          <Button variant="outlined" startIcon={<Receipt />}
            onClick={() => navigate('/cuentas-cobrar/cobros')}>
            Ver Cobros
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tarjetas Resumen */}
      {resumenCartera && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <AccountBalance color="primary" fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Total Facturado</Typography>
                </Box>
                <Typography variant="h6">{fmt(resumenCartera.total_facturado)}</Typography>
                <Typography variant="caption" color="text.secondary">{resumenCartera.cantidad_facturas} facturas</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <CheckCircle color="success" fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Total Cobrado</Typography>
                </Box>
                <Typography variant="h6" color="success.main">{fmt(resumenCartera.total_cobrado)}</Typography>
                <Typography variant="caption" color="text.secondary">{resumenCartera.facturas_pagadas} facturas pagadas</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#fff8e1' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <TrendingUp color="warning" fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Saldo Pendiente</Typography>
                </Box>
                <Typography variant="h6" color="warning.main">{fmt(resumenCartera.saldo_pendiente)}</Typography>
                <Typography variant="caption" color="text.secondary">{resumenCartera.facturas_pendientes} pendientes</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#fce4ec' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Warning color="error" fontSize="small" />
                  <Typography variant="caption" color="text.secondary">Vencido +90 días</Typography>
                </Box>
                <Typography variant="h6" color="error.main">
                  {fmt(resumenCartera.antiguedad_saldos?.['90+'])}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ===== FACTURAS PENDIENTES DE COBRO ===== */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: '#1565c0', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt />
          <Typography variant="h6" fontWeight={700}>Documentos Pendientes de Cobro</Typography>
          <Chip label={facturasPendientes.length} size="small"
            sx={{ bgcolor: '#fff', color: '#1565c0', fontWeight: 700, ml: 1 }} />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5', '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>RUC / Cédula</TableCell>
                <TableCell>No. Documento</TableCell>
                <TableCell align="right">Total Factura</TableCell>
                <TableCell align="right">Cobrado</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell align="center">Antigüedad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facturasPendientes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay documentos pendientes de cobro</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                facturasPendientes.map((f, idx) => {
                  const chipEdad = getChipEdad(f.diasTranscurridos);
                  const parcial = f.cobrado > 0;
                  const clienteRuc = f.cliente?.ruc || f.cliente?.cedula || '';

                  return (
                    <TableRow key={f.id || f.claveAcceso} hover
                      sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa', '& td': { fontSize: '0.78rem', py: 0.8 } }}>
                      <TableCell>{f.fechaEmision}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.cliente?.nombre || f.cliente?.razonSocial || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{clienteRuc}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                        {f.tipo_documento === 'RECIBO'
                          ? <Chip label={`RCV ${f.numero_recibo}`} size="small" color="secondary" variant="outlined" />
                          : String(f.secuencial || '').padStart(9, '0')
                        }
                      </TableCell>
                      <TableCell align="right">{fmt(f.total)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(f.cobrado)}</TableCell>
                      <TableCell align="right">
                        <strong style={{ color: '#d32f2f' }}>{fmt(f.saldo)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={chipEdad.label} color={chipEdad.color} size="small"
                          sx={{ fontSize: '0.65rem', height: 20 }} />
                      </TableCell>
                      <TableCell>
                        {parcial ? (
                          <Chip label="Parcial" color="warning" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                        ) : (
                          <Chip label="Pendiente" color="info" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Registrar Cobro">
                          <IconButton size="small" color="success"
                            onClick={() => navigate(`/cuentas-cobrar/registrar-cobro?clienteId=${clienteRuc || f.cliente?.id}`)}>
                            <Payment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {facturasPendientes.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#e3f2fd', borderTop: '2px solid #1565c0' }}>
            <Typography variant="body2" fontWeight={700}>{facturasPendientes.length} factura(s) pendiente(s)</Typography>
            <Typography variant="body2" fontWeight={700} color="warning.dark">
              Total Pendiente: {fmt(facturasPendientes.reduce((s, f) => s + f.saldo, 0))}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Clientes con Saldo */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Clientes con Saldo Pendiente</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Cliente</strong></TableCell>
                <TableCell><strong>Identificación</strong></TableCell>
                <TableCell align="center"><strong>Facturas Pend.</strong></TableCell>
                <TableCell align="right"><strong>Saldo Total</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientesConSaldo.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">No hay clientes con saldo pendiente</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clientesConSaldo.map(c => (
                  <TableRow key={c.cliente_id} hover>
                    <TableCell>{c.cliente_nombre}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{c.cliente_identificacion}</TableCell>
                    <TableCell align="center">
                      <Chip label={c.facturas_pendientes} size="small" color="warning" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="warning.main">{fmt(c.saldo_total)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Estado de Cuenta">
                        <IconButton size="small" color="primary"
                          onClick={() => navigate(`/cuentas-cobrar/estado-cuenta/${c.cliente_id}`)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Registrar Cobro">
                        <IconButton size="small" color="success"
                          onClick={() => navigate(`/cuentas-cobrar/registrar-cobro?clienteId=${c.cliente_id}`)}>
                          <Payment />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}

export default CuentasPorCobrar;
